import { ChatMessage } from '../types';
import { db } from '../../infrastructure/firebaseConfig';
import { auth } from '../../infrastructure/firebaseConfig';
import { logger } from '../../infrastructure/logger';
import { collection, query, orderBy, addDoc, serverTimestamp, Timestamp, getDocs, writeBatch, updateDoc } from 'firebase/firestore';
import { productQueries, productSearch } from '../../data/products';
import { CHAT, SHIPPING, VALIDATION } from '../constants';
import { BILLING } from '../constants/billing';

const validateUserAccess = (userId: string): boolean => {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;
    return currentUser.uid === userId;
};

export const chatService = {
    /**
     * Subscribes to real-time chat messages in Firestore.
     * ⚠️ SECURITY: Validates that the requesting userId matches the authenticated user.
     * OPTIMIZED: Changed to polling pattern to reduce Firebase reads
     */
    startPollingMessages(userId: string, callback: (messages: ChatMessage[]) => void, intervalMs: number = CHAT.POLLING_INTERVAL_MS): () => void {
        // Security check
        if (!validateUserAccess(userId)) {
            logger.warn('[ChatService] Unauthorized access attempt', { userId });
            callback([]);
            return () => {};
        }

        let errorCount = 0;
        const MAX_ERROR_LOGS = 3;
        
        const fetchData = async () => {
            try {
                const q = query(
                    collection(db, 'chats', userId, 'messages'),
                    orderBy('timestamp', 'asc')
                );
                const snapshot = await getDocs(q);
                const messages = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        text: data.text,
                        sender: data.sender,
                        timestamp: (data.timestamp as Timestamp)?.toDate() || new Date(),
                        read: data.read,
                        buttons: data.buttons
                    } as ChatMessage;
                });
                callback(messages);
                errorCount = 0; // Reset error count on success
            } catch (error) {
                // Limit error logging to avoid console spam
                if (errorCount < MAX_ERROR_LOGS) {
                    logger.error('[ChatService] Error polling messages', error);
                    errorCount++;
                    if (errorCount === MAX_ERROR_LOGS) {
                        logger.warn('[ChatService] Suppressing further polling error logs');
                    }
                }
                callback([]);
            }
        };

        // Initial fetch
        fetchData();

        // Poll every 30 seconds instead of real-time
        const intervalId = setInterval(fetchData, intervalMs);
        return () => clearInterval(intervalId);
    },



    /**
     * Deletes all messages for a user to reset conversation.
     * ⚠️ SECURITY: Validates that the requesting userId matches the authenticated user.
     */
    async clearHistory(userId: string): Promise<void> {
        // Security check
        if (!validateUserAccess(userId)) {
            logger.warn('[ChatService] Unauthorized clearHistory attempt', { userId });
            return;
        }

        const messagesRef = collection(db, 'chats', userId, 'messages');
        const snapshot = await getDocs(messagesRef);
        
        if (snapshot.empty) return;

        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
    },

    /**
     * Sends a real message to Firestore.
     * ⚠️ SECURITY: Validates that the requesting userId matches the authenticated user.
     */
    async sendMessage(userId: string, text: string): Promise<void> {
        // Security check
        if (!validateUserAccess(userId)) {
            logger.warn('[ChatService] Unauthorized sendMessage attempt', { userId });
            return;
        }

        try {
            await addDoc(collection(db, 'chats', userId, 'messages'), {
                text,
                sender: 'user',
                timestamp: serverTimestamp(),
                read: false
            });
        } catch (error) {
            logger.error('[ChatService] Error sending message', error);
            throw error;
        }
    },

    /**
     * Automated bot response logic.
     */
    async simulateBotResponse(userId: string, text: string): Promise<void> {
        const lowerText = text.toLowerCase();
        let reply = "";
        let buttons: { label: string; value: string }[] | undefined = undefined;

        // Reset flow if explicitly requested
        if (lowerText === 'nav_reset') {
            await this.clearHistory(userId);
            // Trigger welcome after clear
            setTimeout(() => this.simulateBotResponse(userId, 'start_flow'), CHAT.BOT_RESPONSE_DELAY_MS);
            return;
        }

        // 1. MAIN FLOWS
        if (lowerText === 'start_flow' || lowerText.includes('hola') || lowerText.includes('buen')) {
            reply = "¡Hola! Un gusto saludarte. Soy el asistente oficial de AllTuc. ¿En qué puedo ayudarte a brillar hoy?";
            buttons = [
                { label: '📦 Mi Pedido', value: 'flow_track' },
                { label: '💰 TucWallet / Saldo', value: 'flow_wallet' },
                { label: '💎 Club Premium', value: 'flow_club' },
                { label: '🚚 Envíos / Local', value: 'flow_info' },
                { label: '📍 Mis Direcciones', value: 'nav_addresses' },
                { label: '💬 Humano', value: 'flow_human' }
            ];
        } 
        else if (lowerText === 'flow_track') {
            reply = "Entiendo, quieres saber por dónde viene tu compra. Podés ver el mapa en vivo y el estado real en 'Mis Envíos'.";
            buttons = [
                { label: 'Ver Seguimiento', value: 'nav_history' },
                { label: '¿Cómo rastrear?', value: 'info_tracking' }
            ];
        }
        else if (lowerText === 'info_tracking') {
            reply = "Es muy fácil: cada pedido tiene un código único (ej: ALLTUC-123). Con ese código podés ver si está en preparación o en camino.";
        }
        else if (lowerText === 'flow_wallet') {
            reply = "Con TucWallet pagas al toque sin usar tarjetas. ¿Querés cargar saldo o ver tu historial de gastos?";
            buttons = [
                { label: 'Cargar Saldo', value: 'nav_topup' },
                { label: 'Ver Movimientos', value: 'nav_wallet_history' }
            ];
        }
        else if (lowerText === 'flow_club') {
            reply = "El Club AllTuc premia tu fidelidad. Cada compra suma TucCoins que podés canjear por beneficios reales.";
            buttons = [
                { label: 'Mis TucCoins', value: 'nav_club' },
                { label: '¿Cómo subo de nivel?', value: 'info_levels' }
            ];
        }
        else if (lowerText === 'info_levels') {
            reply = "Subes de nivel acumulando compras. Nivel Oro te da 10% de descuento extra y Nivel Diamante envío gratis de por vida.";
        }
        else if (lowerText === 'flow_info') {
            reply = "Nuestra logística es la más rápida de la provincia. ¿Qué duda tenés?";
            buttons = [
                { label: 'Costos San Miguel', value: 'info_shipping_smt' },
                { label: 'Interior / Otros', value: 'info_shipping_int' },
                { label: 'Retiro en Local', value: 'info_pickup' }
            ];
        }
        else if (lowerText === 'info_shipping_smt') {
            reply = `En Capital entregamos en el día si compras antes de las ${SHIPPING.SAME_DAY_CUTOFF_HOUR}hs. El costo es de $${SHIPPING.LOCAL_COST.toLocaleString()} o GRATIS si superas los $${SHIPPING.FREE_THRESHOLD.toLocaleString()}.`;
        }
        else if (lowerText === 'info_shipping_int') {
            reply = "Llegamos a Yerba Buena, Tafí Viejo y Lules en 24hs. Para Concepción o Tafí del Valle, entre 48 y 72hs.";
        }
        else if (lowerText === 'info_pickup') {
            reply = "Podés retirar GRATIS en San Martín 650, SMT. Abrimos de corrido de 9 a 21hs. ¡Te esperamos con un mate!";
        }
        else if (lowerText === 'flow_human') {
            reply = "Perfecto, ya le avisé a mi equipo humano. Mientras tanto, decime tu consulta por aquí y te responderemos por WhatsApp o chat.";
        }
        else if (lowerText.includes('venden') || lowerText.includes('tenes') || lowerText.includes('busco')) {
            const queryStr = lowerText.replace(/tenes|tenéis|busco|venden|hay|un|una|la|el|precio/g, '').trim();
            if (queryStr.length > VALIDATION.SEARCH_MIN_LENGTH) {
                const products = await productSearch.searchProducts(queryStr);
                if (products.length > 0) {
                    reply = `¡Tengo buenas noticias! Tenemos "${products[0].title}" disponible a $${products[0].price.toLocaleString()}.`;
                    buttons = [{ label: 'Ver Producto', value: `nav_prod_${products[0].id}` }];
                } else {
                    reply = "No encuentro ese producto exacto ahora, pero seguro tenemos algo parecido en nuestras 20 categorías locales.";
                    buttons = [{ label: 'Explorar Todo', value: 'nav_catalog' }];
                }
            }
        }

        // 2. FALLBACK
        if (!reply) {
            reply = "No estoy seguro de haber entendido bien esa parte. ¿Probamos con alguna de estas opciones?";
            buttons = [
                { label: 'Soporte Técnico', value: 'flow_human' },
                { label: 'Ver Mi Perfil', value: 'nav_profile' },
                { label: 'Volver al Inicio', value: 'start_flow' }
            ];
        }

        // 3. NAVIGATION LOOP INJECTION (Ensure no dead ends)
        if (lowerText !== 'start_flow' && !lowerText.includes('hola') && !lowerText.includes('buen')) {
            if (!buttons) {
                buttons = [
                    { label: '🔄 Menú Principal', value: 'start_flow' },
                    { label: '👋 Gracias, es todo', value: 'nav_close' }
                ];
            } else {
                const hasBackOption = buttons.some(b => b.value === 'start_flow');
                if (!hasBackOption) {
                    buttons.push({ label: '🔙 Menú', value: 'start_flow' });
                }
            }
        }

        // Add bot reply after delay
        setTimeout(async () => {
            try {
                await addDoc(collection(db, 'chats', userId, 'messages'), {
                    text: reply,
                    sender: 'support',
                    timestamp: serverTimestamp(),
                    read: false,
                    buttons: buttons || null
                });
            } catch (error) {
                logger.error('[ChatService] Error sending bot reply', error);
            }
        }, CHAT.BOT_TYPING_DELAY_MS);
    }
};
